import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import { formatDateToLocal } from '@safetyhub/shared';
import { inspectionStatusLabels, inspectionStatusTones, InspectionListItem } from '../../../lib/inspections';
import { Report, getDocStatusBadgeTone } from '../../../lib/reports';
import { resolveReportStatusLabel } from '../../../lib/reportStatusLabel';
import { ChevronIcon, ChevronRightIcon } from './components/Icons';
import { SiteContactsSection } from './components/SiteContactsSection';
import { SiteAgencyLinksSection } from './components/SiteAgencyLinksSection';
import { SiteOperatorGroupItem } from './components/SiteOperatorGroupItem';
import { SiteLocationsSection } from './components/SiteLocationsSection';
import { SiteDetailLocationsSection } from './components/SiteDetailLocationsSection';
import { SiteProcessTypesSection } from './components/SiteProcessTypesSection';
import { SiteEquipmentSection } from './components/SiteEquipmentSection';
import { SiteBusinessOverviewTab } from './components/SiteBusinessOverviewTab';
import { MasterSectionHeader } from './components/MasterSectionHeader';
import { SiteApprovalLinesSection } from './components/SiteApprovalLinesSection';
import { SiteDetailLayoutProps } from './types';
import { useMasterExpanded, useMasterCounts } from './useMasterExpanded';
import { memberDisplayName } from '../../../lib/members';
import { siteIndustryLabel } from '../../../lib/siteIndustry';

/**
 * SiteDetail 모바일 레이아웃
 */
export function SiteDetailMobile({
  data,
  companyId,
  siteId,
  state,
  permissions,
  isFold,
}: SiteDetailLayoutProps) {
  const { site, assignments, members, findingsTrend, siteStats, recentInspections, recentReports } =
    data;
  const {
    expandedSections,
    toggleSection,
    trendPeriod,
    setTrendPeriod,
    activeTab,
    setActiveTab,
  } = state;
  // 자리마다 키 하나(P4 ②): 하위 섹션 prop 이름 `isOwner`는 호환용이고 값은 canManageSite(site.manage)
  //
  // ⚠️ 점검 담당자 배정은 `canEdit`(site.manage ∥ 정담당자)이 아니라 **`canAssign`(소유자 성질)**이다 —
  // 서버가 `site.assign` 키(컨트롤러 가드) **∧** `isOwnerMember`(sites.service assignMember)를 둘 다 요구한다.
  // 종전엔 이 화면만 `canEdit`을 읽어 `site.manage` 보유자(시드 safety-manager)가 **휴대폰·폴드에서만**
  // 버튼을 보고 403을 받았다(데스크톱은 처음부터 `canAssign`). `canEdit`은 「현장 정보 수정」 전용이고
  // 그 자리는 index.tsx의 StickyHeader 액션이다(서버 `SitesService.update`와 같은 OR 판정).
  //
  // ⚠️ 그리고 아래 `isOwner`는 **권한 키 `site.manage`의 별칭**이다 — 소유권 **성질**(서버
  // `isOwnerMember`)이 아니다. `isOwner={isOwner}` 전달 자리들의 값도 전부 그 키다. 이름을 근거로
  // 성질 축으로 「정리」하면(`usePermissions().isOwner`로 바꾸면) 그 키만 가진 시드 안전관리자가
  // 장소·공정·설비·담당자 섹션의 입구를 한꺼번에 잃는다 — 2026-09-24 실사고와 같은 형태다. 같은
  // 이유로 결재선 prop은 2026-09-27에 `canManageApprovalLines`로 이름을 고쳤다. 별칭 자체를 걷는
  // 것(10곳+ rename)은 backlog. 배선 핀: `components/__tests__/rbacOwnerAxisWiringGuard.test.ts`.
  const { canAssign, canManageSite: isOwner, canManageApprovalLines } = permissions;

  const { detailTab, setDetailTab } = state;
  const [isMasterExpanded, toggleMaster] = useMasterExpanded();
  const masterCounts = useMasterCounts(companyId, siteId, detailTab === 'management');

  return (
    <div className="space-y-4">
      {/* 페이지 탭 헤더 */}
      <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
        <nav className="flex border-b border-line">
          <button
            onClick={() => setDetailTab('basic')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              detailTab === 'basic'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-ink-500'
            }`}
          >
            기본 정보
          </button>
          <button
            onClick={() => setDetailTab('overview')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              detailTab === 'overview'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-ink-500'
            }`}
          >
            사업장 개요
          </button>
          <button
            onClick={() => setDetailTab('management')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              detailTab === 'management'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-ink-500'
            }`}
          >
            관리
          </button>
          <button
            onClick={() => setDetailTab('approval')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 ${
              detailTab === 'approval'
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-ink-500'
            }`}
          >
            결재선
          </button>
        </nav>
      </Card>

      {detailTab === 'approval' ? (
        <SiteApprovalLinesSection
          companyId={companyId}
          siteId={siteId}
          canManageApprovalLines={canManageApprovalLines}
        />
      ) : detailTab === 'overview' ? (
        <SiteBusinessOverviewTab
          companyId={companyId}
          siteId={siteId}
          site={site}
          isOwner={isOwner}
        />
      ) : detailTab === 'management' ? (
        <div className="space-y-6">
          {/* 대상 구성 마스터 — 펼침/접힘 가능 */}
          <section>
            <MasterSectionHeader
              expanded={isMasterExpanded}
              onToggle={toggleMaster}
              processCount={masterCounts.processCount}
              locationCount={masterCounts.locationCount}
              detailLocationCount={masterCounts.detailLocationCount}
              size="sm"
            />
            {isMasterExpanded && (
              /* phone: 항상 1컬럼. fold 펼침(sm+): 3컬럼. fold 접힘(<sm): 1컬럼. */
              <div className={`grid grid-cols-1 gap-4 ${isFold ? 'sm:grid-cols-3' : ''}`}>
                <SiteProcessTypesSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
                <SiteLocationsSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
                <SiteDetailLocationsSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
              </div>
            )}
          </section>

          {/* 대상 (풀폭) */}
          <section>
            <SiteEquipmentSection companyId={companyId} siteId={siteId} isOwner={isOwner} />
          </section>
        </div>
      ) : (
      <>
      {/* 기본 정보 (접이식) */}
      <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
        <button
          onClick={() => toggleSection('basicInfo')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <h2 className="text-base font-medium text-ink-900">기본 정보</h2>
          <ChevronIcon
            className={`h-5 w-5 text-ink-400 transition-transform ${expandedSections.basicInfo ? 'rotate-180' : ''}`}
          />
        </button>
        {expandedSections.basicInfo && (
          <div className="px-4 pb-4 border-t border-line">
            <dl className="grid grid-cols-2 gap-3 pt-3">
              <div>
                <dt className="text-xs text-ink-500">업종</dt>
                <dd className="text-sm text-ink-900">{siteIndustryLabel(site) ?? '-'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-500">시간대</dt>
                <dd className="text-sm text-ink-900">{site.timezone || '기본값'}</dd>
              </div>
              <SiteOperatorGroupItem
                site={site}
                dtClassName="text-xs text-ink-500"
                ddClassName="text-sm text-ink-900"
              />
              <div>
                <dt className="text-xs text-ink-500">등록일</dt>
                <dd className="text-sm text-ink-900">
                  {formatDateToLocal(new Date(site.createdAt))}
                </dd>
              </div>
              {(site.latitude || site.longitude) && (
                <div>
                  <dt className="text-xs text-ink-500">좌표</dt>
                  <dd className="text-sm text-ink-900 truncate">
                    {site.latitude}, {site.longitude}
                  </dd>
                </div>
              )}
            </dl>
            {site.hazardCategories && site.hazardCategories.length > 0 && (
              <div className="mt-3 pt-3 border-t border-line">
                <dt className="text-xs text-ink-500 mb-1">위험 카테고리</dt>
                <dd className="flex flex-wrap gap-1">
                  {site.hazardCategories.map((cat) => (
                    <Badge key={cat} tone="amber">
                      {cat}
                    </Badge>
                  ))}
                </dd>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 통계 요약 (가로 배치) */}
      <div className="flex gap-2">
        <Link
          to={`/inspections?siteId=${siteId}`}
          className="flex-1 text-center py-3 px-2 bg-brand-50 rounded-card"
        >
          <span className="text-lg font-bold text-brand-600">
            {siteStats?.inspections.total ?? '-'}
          </span>
          <span className="text-xs text-ink-600 ml-1">점검</span>
          {siteStats && siteStats.inspections.inProgress > 0 && (
            <span className="block text-xs text-brand-500">
              진행 {siteStats.inspections.inProgress}
            </span>
          )}
        </Link>
        <Link
          to={`/reports?siteId=${siteId}`}
          className="flex-1 text-center py-3 px-2 bg-green-50 rounded-card"
        >
          <span className="text-lg font-bold text-green-600">
            {siteStats?.reports.total ?? '-'}
          </span>
          <span className="text-xs text-ink-600 ml-1">보고서</span>
          {siteStats && siteStats.reports.pendingApproval > 0 && (
            <span className="block text-xs text-yellow-600">
              대기 {siteStats.reports.pendingApproval}
            </span>
          )}
        </Link>
        {/* TBM — 데스크톱과 같은 축(`siteStats.tbms`). 링크가 아닌 이유도 같다:
            목록에 「TBM만」 거르는 필터가 없어 링크를 걸면 숫자와 도착지가 어긋난다. */}
        <div className="flex-1 text-center py-3 px-2 bg-surface-soft rounded-card">
          <span className="text-lg font-bold text-ink-700">
            {siteStats?.tbms.total ?? '-'}
          </span>
          <span className="text-xs text-ink-600 ml-1">TBM</span>
          {siteStats && siteStats.tbms.inProgress > 0 && (
            <span className="block text-xs text-brand-500">
              교육 {siteStats.tbms.inProgress}
            </span>
          )}
        </div>
      </div>

      {/* 위험요인 현황 (상단 배치) */}
      {findingsTrend && findingsTrend.periods.length > 0 && (
        <Card padding="md" radius="lg" elevation="md">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-medium text-ink-900">위험요인 현황</h2>
            <select
              value={trendPeriod}
              onChange={(e) => setTrendPeriod(e.target.value as typeof trendPeriod)}
              className="text-xs border-ink-300 rounded-control"
            >
              <option value="daily">7일</option>
              <option value="weekly">5주</option>
              <option value="monthly">6개월</option>
            </select>
          </div>

          {/* 신규 발견 그래프 */}
          <div className="mb-4">
            <h3 className="text-xs font-medium text-ink-500 mb-2">신규 발견</h3>
            <div className="space-y-1.5">
              {findingsTrend.periods.slice(-5).map((period, idx) => {
                const maxValue = Math.max(
                  ...findingsTrend.periods.map((p) => p.discovered),
                  1
                );
                const width = (period.discovered / maxValue) * 100;

                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-ink-500 w-12 flex-shrink-0 truncate">
                      {period.label}
                    </span>
                    <div className="flex-1 bg-ink-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-3 rounded-full bg-brand-500"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="text-xs text-ink-600 w-5 text-right">{period.discovered}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 미해결 누적 (간소화) */}
          <div>
            <h3 className="text-xs font-medium text-ink-500 mb-2">미해결 누적</h3>
            <div className="flex items-end gap-1 h-16">
              {findingsTrend.periods.slice(-7).map((period, idx) => {
                const maxValue = Math.max(
                  ...findingsTrend.periods.map((p) => p.unresolved),
                  1
                );
                const height = (period.unresolved / maxValue) * 100;

                return (
                  <div
                    key={idx}
                    className="flex-1 bg-brand-200 rounded-t"
                    style={{ height: `${Math.max(height, 5)}%` }}
                    title={`${period.label}: ${period.unresolved}`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-ink-400 mt-1">
              <span>
                {findingsTrend.periods[Math.max(0, findingsTrend.periods.length - 7)]?.label}
              </span>
              <span>{findingsTrend.periods[findingsTrend.periods.length - 1]?.label}</span>
            </div>
          </div>
        </Card>
      )}

      {/* 점검 담당자 */}
      <Card padding="xl" radius="lg" elevation="md">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-ink-900">점검 담당자</h2>
            <p className="text-xs text-ink-400 mt-1">이 현장에 배정된 안전관리자 목록</p>
          </div>
          {canAssign && (
            <button
              onClick={() => state.setShowAssignModal(true)}
              className="text-sm text-brand-600 hover:text-brand-800"
            >
              + 추가
            </button>
          )}
        </div>
        {assignments.length === 0 ? (
          <p className="text-sm text-ink-500">할당된 담당자가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-line">
            {assignments.map((assignment) => {
              const member = members.find((m) => m.id === assignment.memberId);
              if (!member) return null;
              const isPrimary = assignment.assignmentType === 'PRIMARY';
              return (
                <li
                  key={assignment.id}
                  className="py-3 flex items-center cursor-pointer hover:bg-surface-hover -mx-2 px-2 rounded-card transition-colors"
                  onClick={() => state.setSelectedAssignment(assignment)}
                >
                  <div className="flex-shrink-0 h-9 w-9 rounded-full bg-brand-100 flex items-center justify-center">
                    <span className="text-brand-600 font-medium text-sm">
                      {memberDisplayName(member, '?').charAt(0)}
                    </span>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {/* 이름 정본은 Member — user는 로그인 계정이 있어야만 존재한다(lib/members). */}
                      <p className="text-sm font-medium text-ink-900 truncate">{memberDisplayName(member)}</p>
                      {/* 정(PRIMARY)/부(SECONDARY)는 색으로 가르는 짝이라 tone만 다르게 준다 */}
                      <Badge
                        tone={isPrimary ? 'brand' : 'slate'}
                        shape="rounded"
                        size="sm"
                        className="flex-shrink-0"
                      >
                        {isPrimary ? '정' : '부'}
                      </Badge>
                    </div>
                  </div>
                  <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400 ml-2" />
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* 현장 담당자 */}
      <SiteContactsSection
        companyId={companyId}
        siteId={siteId}
        isOwner={isOwner}
      />

      {/* 대행 기관(P4) — 플래그 꺼짐·링크 0건이면 섹션째 안 그린다 */}
      <SiteAgencyLinksSection companyId={companyId} siteId={siteId} />

      {/* 탭 (점검/보고서/TBM) */}
      <Card padding="none" radius="lg" elevation="md">
        <div className="border-b border-line">
          <nav className="flex">
            {[
              { key: 'inspections', label: '점검' },
              { key: 'reports', label: '보고서' },
              { key: 'tbm', label: 'TBM' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex-1 py-3 text-sm font-medium border-b-2 ${
                  activeTab === tab.key
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="p-4">
          {activeTab === 'inspections' && (
            <div className="space-y-2">
              {!recentInspections || recentInspections.length === 0 ? (
                <p className="text-sm text-ink-500 text-center py-4">점검 내역이 없습니다.</p>
              ) : (
                <>
                  {recentInspections.map((inspection: InspectionListItem) => (
                    <Link
                      key={inspection.id}
                      to={`/inspections/${inspection.id}`}
                      className="block p-3 bg-surface-soft rounded-card hover:bg-surface-hover"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-ink-900 truncate">
                          {site.name} {inspection.sequenceNumber ?? 1}차 점검
                        </span>
                        <Badge tone={inspectionStatusTones[inspection.status]}>
                          {inspectionStatusLabels[inspection.status]}
                        </Badge>
                      </div>
                      <p className="text-xs text-ink-500 mt-1">
                        {formatDateToLocal(new Date(inspection.createdAt))}
                      </p>
                    </Link>
                  ))}
                  <Link
                    to={`/inspections?siteId=${siteId}`}
                    className="flex items-center justify-center gap-1 py-2 text-sm text-brand-600 hover:underline"
                  >
                    더보기
                    <ChevronRightIcon className="h-4 w-4" />
                  </Link>
                </>
              )}
            </div>
          )}
          {activeTab === 'reports' && (
            <div className="space-y-2">
              {!recentReports || recentReports.length === 0 ? (
                <p className="text-sm text-ink-500 text-center py-4">보고서가 없습니다.</p>
              ) : (
                <>
                  {recentReports.map((report: Report) => (
                    <Link
                      key={report.id}
                      to={`/reports/${report.id}`}
                      className="block p-3 bg-surface-soft rounded-card hover:bg-surface-hover"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-ink-900 truncate">
                          {report.title}
                        </span>
                        {/* 라벨은 report 데이터로 판정 — 결재 정책 없는 완료본은 '완료'(목록 응답도 서명 행을 싣는다). */}
                        <Badge tone={getDocStatusBadgeTone(report.status)}>
                          {resolveReportStatusLabel(report)}
                        </Badge>
                      </div>
                      <p className="text-xs text-ink-500 mt-1">
                        {formatDateToLocal(new Date(report.createdAt))}
                      </p>
                    </Link>
                  ))}
                  <Link
                    to={`/reports?siteId=${siteId}`}
                    className="flex items-center justify-center gap-1 py-2 text-sm text-brand-600 hover:underline"
                  >
                    더보기
                    <ChevronRightIcon className="h-4 w-4" />
                  </Link>
                </>
              )}
            </div>
          )}
          {activeTab === 'tbm' && (
            <p className="text-sm text-ink-500 text-center py-4">TBM 기능 준비중입니다.</p>
          )}
        </div>
      </Card>

      </>
      )}
    </div>
  );
}

export default SiteDetailMobile;
